import {CoreLogger, type CreateExtArgs, DAL, pointerUtils} from '@wix/document-manager-core'
import type {ViewerManager} from '@wix/viewer-manager-adapter'
import type {Pointer, Pointers, Layout, SimpleDalAPI, CompRef} from '@wix/document-services-types'
import _ from 'lodash'
import {displayedOnlyStructureUtil} from '@wix/santa-core-utils'
import {ReportableError} from '@wix/document-manager-utils'
import {getRepeatersNestingSuffix} from './inflationUtils'
import type {DataModelExtensionAPI} from '../extensions/dataModel/dataModel'
import {COMP_TYPES, DATA_TYPES} from '../constants/constants'
import {getComponentType} from './dalUtils'
import type {ComponentDefinitionExtensionAPI} from '../extensions/componentDefinition'
const {
    getRepeaterItemId,
    getRepeaterTemplateId,
    getUniqueDisplayedId,
    getUniqueStructure,
    isRepeatedComponent,
    isRefPointer
} = displayedOnlyStructureUtil

export const repeaterDelimiter = '__'

interface SimpleDalPointers {
    dal: SimpleDalAPI
    pointers: Pointers
}

const {getInnerPointer, getRepeatedItemPointerIfNeeded} = pointerUtils

const isRepeaterType = (type: string): boolean => type === 'RepeaterContainer'

const isRepeater = (dal: SimpleDalAPI, pointer: Pointer) => {
    const compTypePointer = getInnerPointer(getRepeatedItemPointerIfNeeded(pointer), ['type'])
    return isRepeaterType(dal.get(compTypePointer))
}

const isRepeaterMasterItem = ({dal, pointers}: SimpleDalPointers, pointer: Pointer) => {
    if (!isRepeatedComponent(pointer.id)) {
        return false
    }
    const compPointer = pointers.getOriginalPointerFromInner(pointer)
    const repeaterPointer = pointers.structure.getAncestorByPredicate(compPointer, parentPtr =>
        isRepeater(dal, parentPtr)
    )
    const itemId = getRepeatersNestingSuffix(pointer.id)
    const templateItemPointer = pointers.displayedOnlyComponents.getComponentTemplateId(repeaterPointer.id)
    const currentMasterItem = dal.get(templateItemPointer)

    return itemId === currentMasterItem
}

const mergeLayout = (pointer: Pointer, currentLayout: Layout, newLayout: Layout, logger: CoreLogger) => {
    const mergedLayout = _.merge({}, currentLayout, newLayout)
    if (!_.isFinite(mergedLayout.x) || !_.isFinite(mergedLayout.y)) {
        logger.captureError(
            new ReportableError({
                message: 'illegal layout when update master item',
                errorType: 'RepeaterTemplateLayoutError',
                extras: {
                    pointer,
                    currentLayout,
                    newLayout
                }
            })
        )
        mergedLayout.x = _.isFinite(newLayout.x) ? newLayout.x : currentLayout.x
        mergedLayout.y = _.isFinite(newLayout.y) ? newLayout.y : currentLayout.y
    }
    return mergedLayout
}

function syncTemplateLayout(
    {dal, viewerManager, logger}: {dal: DAL; viewerManager: ViewerManager; logger: CoreLogger},
    compPointer: Pointer,
    itemsToUpdate: Pointer[]
) {
    _(itemsToUpdate)
        .reject(isRefPointer)
        .map(descendantPointer => [descendantPointer, getRepeatedItemPointerIfNeeded(descendantPointer)])
        .filter(([, descendantPointerTemplate]) => !!dal.get(descendantPointerTemplate))
        .forEach(([descendantPointer, descendantPointerTemplate]) => {
            if (!viewerManager.dal.isExist(descendantPointer)) {
                logger.captureError(
                    new ReportableError({
                        message: 'missing descendant when updating master item',
                        errorType: 'RepeaterTemplateMissingDescendantError',
                        extras: {
                            pointer: descendantPointer
                        }
                    })
                )
                return
            }
            const layoutPointerToUpdate = getInnerPointer(descendantPointerTemplate, 'layout')
            const currentLayout = viewerManager.dal.get(getInnerPointer(descendantPointer, 'layout'))
            const compMeasures = viewerManager.viewerSiteAPI.getBasicMeasureForComp(descendantPointer)
            const newLayout = mergeLayout(descendantPointer, currentLayout, compMeasures, logger)
            dal.set(layoutPointerToUpdate, newLayout)
        })
}

const updateRepeaterMasterItem = (
    {
        pointers,
        dal,
        viewerManager,
        logger
    }: {dal: DAL; pointers: Pointers; viewerManager: ViewerManager; logger: CoreLogger},
    pointer: Pointer
) => {
    if (!isRepeatedComponent(pointer.id)) {
        return
    }

    //get the parent repeater
    let componentPointer = pointers.getOriginalPointerFromInner(pointer)
    let parentPointer = pointers.components.getParent(componentPointer)
    while (isRepeatedComponent(parentPointer.id)) {
        componentPointer = pointers.getOriginalPointerFromInner(parentPointer)
        parentPointer = pointers.components.getParent(componentPointer)
    }

    /// update repeater template item map
    const itemId = getRepeatersNestingSuffix(componentPointer.id)
    const templateItemPointer = pointers.displayedOnlyComponents.getComponentTemplateId(parentPointer.id)
    const currentTemplateItem = dal.get(templateItemPointer)
    if (currentTemplateItem === itemId) {
        return
    }
    dal.set(templateItemPointer, itemId)

    syncTemplateLayout(
        {dal, viewerManager, logger},
        componentPointer,
        pointers.components.getChildrenRecursivelyRightLeftRootIncludingRoot(componentPointer)
    )
}

const getRepeaterItems = ({pointers, dal, extensionAPI}: CreateExtArgs, compPointer: Pointer) => {
    const repeaterPointer = pointers.structure.getAncestorByPredicate(compPointer, (parentPointer: Pointer) =>
        isRepeater(dal, parentPointer)
    )
    const {dataModel} = extensionAPI as DataModelExtensionAPI
    return _.get(dataModel.components.getItem(repeaterPointer, DATA_TYPES.data), ['items'], [])
}

const getRepeaterAncestor = (
    {extensionAPI, pointers, dal}: Pick<CreateExtArgs, 'extensionAPI' | 'pointers'> & {dal: SimpleDalAPI},
    compPointer: CompRef
): CompRef | undefined => {
    const {componentDefinition} = extensionAPI as ComponentDefinitionExtensionAPI

    return pointers.structure.getAncestorByPredicate(compPointer, (parentPointer: CompRef) =>
        componentDefinition.isRepeater(getComponentType(dal, parentPointer))
    ) as CompRef
}

const getStaticRepeaterAncestor = (
    {pointers, dal}: Pick<CreateExtArgs, 'pointers'> & {dal: SimpleDalAPI},
    compPointer: CompRef
) =>
    pointers.structure.getAncestorByPredicate(
        compPointer,
        (parentPointer: CompRef) => getComponentType(dal, parentPointer) === COMP_TYPES.REPEATER
    )

const isDynamicRepeater = (dal: SimpleDalAPI, repeaterPointer: CompRef) =>
    getComponentType(dal, repeaterPointer) === COMP_TYPES.DYNAMIC_REPEATER

const getDynamicRepeaterAncestor = (
    {pointers, dal}: Pick<CreateExtArgs, 'pointers'> & {dal: SimpleDalAPI},
    compPointer: CompRef
) =>
    pointers.structure.getAncestorByPredicate(compPointer, (parentPointer: CompRef) =>
        isDynamicRepeater(dal, parentPointer)
    )

const isStaticallyRepeatedComponent = (
    createExtArgs: Pick<CreateExtArgs, 'extensionAPI' | 'pointers'> & {dal: SimpleDalAPI},
    compPointer: CompRef
) => {
    if (!isRepeatedComponent(compPointer.id)) {
        return false
    }

    return !!getStaticRepeaterAncestor(createExtArgs, pointerUtils.getRepeatedItemPointerIfNeeded(compPointer))
}

const getTemplateCompRefIfDynamicallyRepeated = (
    createExtArgs: Pick<CreateExtArgs, 'extensionAPI' | 'pointers'> & {dal: SimpleDalAPI},
    compPointerToCheck: CompRef,
    compPointerToModify: CompRef = compPointerToCheck
) => {
    if (
        !isRepeatedComponent(compPointerToModify.id) ||
        isStaticallyRepeatedComponent(createExtArgs, compPointerToCheck)
    ) {
        return compPointerToModify
    }

    return pointerUtils.getRepeatedItemPointerIfNeeded(compPointerToModify)
}

export {
    isDynamicRepeater,
    isRepeaterType,
    isRepeater,
    getDynamicRepeaterAncestor,
    isRepeatedComponent,
    getRepeaterTemplateId,
    getRepeaterItemId,
    getUniqueDisplayedId,
    getUniqueStructure,
    getTemplateCompRefIfDynamicallyRepeated,
    isRepeaterMasterItem,
    updateRepeaterMasterItem,
    syncTemplateLayout,
    getRepeaterItems,
    getRepeaterAncestor
}
